import React, { useState } from 'react';
import { ArrowRight, BookOpen, Factory, LockKeyhole, Mail, PlusCircle, Trash2, Wrench, X } from 'lucide-react';

import type {
  GenerationEquipmentScreenRow,
} from '../../../lib/generationImpact/generationEquipmentCatalog';
import type {
  GenerationUserEquipmentScreenRow,
} from '../../../lib/generationImpact/userEquipmentEnvelope';
import { USER_EQUIPMENT_STANDARD_DATA_REQUESTS } from '../../../lib/generationImpact/userEquipmentEnvelope';
import { buildOemDataRequestEmail } from '../../../lib/generationImpact/oemDataRequestEmail';
import {
  deriveGenerationEquipmentConstraintFits,
  type GenerationEquipmentConstraintContext,
  type GenerationEquipmentConstraintFit,
} from '../../../lib/generationImpact/workflowGuidance';
import type {
  GenerationBomItemBasis,
  GenerationEquipmentExhibitBasis,
  GenerationImpactResult,
} from '../../../types/generationImpact';

interface GenerationEquipmentEnvelopeBoardProps {
  /** Suggested (admitted) sourced-catalog envelopes. */
  rows: readonly GenerationEquipmentScreenRow[];
  /** User-supplied, unverified entries screened through the same solver. */
  userRows: readonly GenerationUserEquipmentScreenRow[];
  /** Which suggested envelopes the user picked into their working set. */
  shortlistedIds: readonly string[];
  currentResult: GenerationImpactResult;
  /** Grid-only supply is reviewed as declared feeds, without a fuel-generation shortlist. */
  gridOnly?: boolean;
  /** Frozen BOM evidence remains a net-nameplate reference, never a solver rating. */
  linkedBomItems?: readonly GenerationBomItemBasis[];
  selected?: GenerationEquipmentExhibitBasis;
  readOnly?: boolean;
  actionsDisabled?: boolean;
  appliedStatus?: string | null;
  siteName: string;
  /** Documented site-envelope limits; enables per-card constraint-fit chips. */
  constraintContext?: GenerationEquipmentConstraintContext;
  /** Jump to the Site envelope stage (undocumented-limit chips link there). */
  onOpenSiteEnvelope?: () => void;
  /**
   * Focus the acoustic source schedule on THIS step. Vendor sound levels are
   * equipment evidence, so a missing source level is fixed here — never in the
   * Step 2 site envelope, which carries only the receptor facts.
   */
  onOpenAcousticSources?: () => void;
  /** Opens the unified Equipment Library (BOM · OEM · catalog · manual). */
  onOpenLibrary?: () => void;
  /** Proactive BOM→selection bridge shown when lines are unrepresented. */
  bomBridge?: {
    unrepresentedCount: number;
    generationLineCount: number;
    onReview: () => void;
    onDismiss: () => void;
  };
  onApply: (catalogId: string) => void;
  /**
   * Open the reconciliation form for an entry whose published ratings conflict.
   * Without this the flagged row is a dead end — the gate states a problem the
   * user has no way to act on.
   */
  onReconcile?: (catalogId: string) => void;
  onToggleShortlist: (catalogId: string, shortlisted: boolean) => void;
  onAddEquipment: () => void;
  onApplyUserEquipment: (entryId: string) => void;
  onRemoveUserEquipment: (entryId: string) => void;
}

function number(value: number, maximumFractionDigits = 0): string {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits,
    minimumFractionDigits: maximumFractionDigits,
  }).format(value);
}

function signed(value: number): string {
  return `${value > 0 ? '+' : ''}${number(value, 1)}`;
}

function ratingScope(scope: GenerationEquipmentScreenRow['publishedRatingScope']): string {
  if (scope === 'net-plant') return 'net plant';
  if (scope === 'gross-plant') return 'gross plant';
  return 'generator terminals';
}

function sourceKind(kind: GenerationEquipmentScreenRow['sourceKind']): string {
  if (kind === 'oem-rating') return 'OEM rating';
  if (kind === 'project-precedent') return 'Project precedent';
  return 'Derived aggregation';
}

function unresolvedFields(row: GenerationEquipmentScreenRow): string {
  const labels: Array<[keyof GenerationEquipmentScreenRow['dataStatus'], string]> = [
    ['ambientCorrectionCurve', 'ambient curve'],
    ['coolingHeatBalance', 'cooling'],
    ['fuelGasPressure', 'gas pressure'],
    ['acousticSourceData', 'sound power'],
    ['installedPlantFootprint', 'footprint'],
    ['ccsIntegration', 'CCS'],
    ['reliabilityAndMaintenance', 'reliability'],
  ];
  return labels
    .filter(([key]) => row.dataStatus[key] !== 'published')
    .map(([, label]) => label)
    .join(' · ');
}

function rowTone(row: GenerationEquipmentScreenRow): string {
  if (row.confirmationRequired) return 'border-l-warning';
  if (row.result.capacity.nMinusOneReserveVerdict === 'pass') return 'border-l-success';
  if (row.result.capacity.nMinusOneVerdict === 'pass') return 'border-l-warning';
  return 'border-l-danger';
}

/**
 * Equipment selection board — the WORKING SET only. Discovery, ranking, and
 * comparison live in the Equipment Library dialog (Duty-First Finder); this
 * board shows what the user has picked, plus the active duty envelope. All
 * calculations arrive from the canonical generation solver; the component
 * only labels published, planning, and user bases.
 */
export function GenerationEquipmentEnvelopeBoard({
  rows,
  userRows,
  shortlistedIds,
  currentResult,
  gridOnly = false,
  linkedBomItems = [],
  selected,
  readOnly = false,
  actionsDisabled = false,
  appliedStatus = null,
  siteName,
  constraintContext,
  onOpenSiteEnvelope,
  onOpenAcousticSources,
  onOpenLibrary,
  bomBridge,
  onApply,
  onReconcile,
  onToggleShortlist,
  onAddEquipment,
  onApplyUserEquipment,
  onRemoveUserEquipment,
}: GenerationEquipmentEnvelopeBoardProps): React.JSX.Element {
  const selectedUnit = currentResult.unitResults[0];
  const selectedBasis = selected?.selection;
  const selectedBlockCount = selectedBasis?.screeningNormalization.appliedBlockCount ?? 0;
  const currentBlockCount = Math.max(
    1,
    currentResult.unitResults.reduce((sum, unit) => sum + unit.count, 0),
  );
  const [copiedRequestKey, setCopiedRequestKey] = useState<string | null>(null);

  const shortlistedSet = new Set(shortlistedIds);
  // An already-applied basis is part of the working set even on legacy saves
  // that predate the shortlist field.
  if (selectedBasis) shortlistedSet.add(selectedBasis.catalogId);
  const workingRows = rows.filter(row => shortlistedSet.has(row.catalogId));

  if (gridOnly && workingRows.length === 0 && userRows.length === 0) {
    return (
      <section aria-label="Grid supply equipment" className="min-w-0 max-w-full border-2 border-border bg-card" data-testid="generation-equipment-envelope-board">
        <div className="border-b border-border px-4 py-3">
          <h2 className="text-sm font-bold text-text-primary">Grid supply and linked equipment</h2>
          <p className="mt-1 max-w-3xl text-xs leading-relaxed text-text-muted">Capacity uses the declared feed ratings and contingency groups. Separate rows do not verify independent utility service.</p>
        </div>
        <div className="space-y-4 px-4 py-3" data-testid="generation-equipment-grid-supply">
          <div>
            <h3 className="mb-2 text-xs font-bold text-text-secondary">Declared grid feeds</h3>
            <ul className="divide-y divide-border text-xs">
              {currentResult.unitResults.map(unit => (
                <li key={unit.unitId} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <span className="text-text-primary">{unit.label}</span>
                  <span className="tabular-nums text-text-secondary">{number(unit.totalNetMW, 1)} MW delivered</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-xs font-bold text-text-secondary">Linked Equipment BOM</h3>
              {bomBridge && (
                <button type="button" disabled={actionsDisabled} onClick={bomBridge.onReview} className="border border-primary px-3 py-1.5 text-xs text-primary outline-none hover:bg-primary/5 focus-visible:ring-2 focus-visible:ring-primary disabled:border-border disabled:text-text-muted rounded-md">Review Equipment BOM</button>
              )}
            </div>
            {linkedBomItems.length > 0 ? (
              <ul className="divide-y divide-border text-xs" data-testid="generation-equipment-grid-linked-bom">
                {linkedBomItems.map(item => (
                  <li key={item.bomItemId} className="flex flex-wrap items-start justify-between gap-2 py-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-text-primary">{item.name}</p>
                      <p className="mt-1 text-text-muted">{item.quantity.toLocaleString('en-US', { maximumFractionDigits: 8 })} {item.unit}</p>
                    </div>
                    <span className="tabular-nums text-text-secondary">{item.totalNetNameplateMW == null ? 'Capacity not specified' : `${number(item.totalNetNameplateMW, 1)} MW net nameplate (reference)`}</span>
                  </li>
                ))}
              </ul>
            ) : <p className="text-xs text-text-muted">No generation equipment is linked to this scenario.</p>}
            <p className="mt-3 max-w-3xl text-xs leading-relaxed text-text-muted">Linked standby generators and fuel tanks are reference equipment. Standby operation, fuel use and emissions are excluded from this grid-only result.</p>
          </div>
        </div>
      </section>
    );
  }

  // Constraint fits per card. A limit that is `undocumented` for EVERY card is
  // a property of the site envelope, not of any machine — hoist it to one
  // fleet-level line so the cards carry only the per-machine fits/exceeds.
  const cardFits = new Map<string, readonly GenerationEquipmentConstraintFit[]>();
  if (constraintContext) {
    for (const row of workingRows) {
      cardFits.set(row.catalogId, deriveGenerationEquipmentConstraintFits(row.result, constraintContext));
    }
    for (const row of userRows) {
      if (row.result) cardFits.set(row.entry.id, deriveGenerationEquipmentConstraintFits(row.result, constraintContext));
    }
  }
  const hoistedUndocumented: GenerationEquipmentConstraintFit[] = (() => {
    const all = [...cardFits.values()];
    if (all.length === 0) return [];
    const [first] = all;
    return first
      .filter(fit => fit.status === 'undocumented'
        && all.every(fits => fits.find(candidate => candidate.id === fit.id)?.status === 'undocumented'));
  })();
  const hoistedIds = new Set(hoistedUndocumented.map(fit => fit.id));
  // "Undocumented" covers two different situations and the Step 2 checklist
  // only sees the first: (a) the LIMIT is missing, (b) the limit exists but
  // the fleet-side figure cannot be formed yet (no facility cooling basis, no
  // vendor source level). Name the second so Step 2 "documented" and Step 3
  // "undocumented" never appear to contradict each other.
  const hoistedQualifier = (fit: GenerationEquipmentConstraintFit): string | null => {
    if (!constraintContext) return null;
    if (fit.id === 'water-capacity' && constraintContext.availableWaterConsumptionGalPerDay != null) return 'limit documented · needs facility cooling basis';
    if (fit.id === 'receptor-noise' && constraintContext.receptors.length > 0) return 'receptor documented · needs source sound level';
    if (fit.id === 'co2-export' && constraintContext.ccsExportCapacityTonnesPerYear != null) return 'capacity documented · export route unconfirmed';
    return null;
  };
  // WHERE the user actually fixes this fit. A missing vendor sound level is
  // equipment evidence and lives on THIS step's acoustic source schedule —
  // routing it to the Step 2 site envelope lands the user on a step that no
  // longer holds the field (an unrecoverable dead end).
  const needsAcousticSources = (fit: GenerationEquipmentConstraintFit): boolean =>
    fit.id === 'receptor-noise'
    && (constraintContext?.receptors.length ?? 0) > 0
    && onOpenAcousticSources != null;
  const fitAction = (fit: GenerationEquipmentConstraintFit): (() => void) | undefined =>
    (needsAcousticSources(fit) ? onOpenAcousticSources : onOpenSiteEnvelope);
  const fitDestinationLabel = (fit: GenerationEquipmentConstraintFit): string =>
    (needsAcousticSources(fit) ? 'Source levels' : 'Site envelope');

  const copyRequest = async (key: string, to: string, subject: string, body: string): Promise<void> => {
    const text = `To: ${to || '(fill in OEM contact)'}\nSubject: ${subject}\n\n${body}`;
    try {
      await navigator.clipboard?.writeText(text);
      setCopiedRequestKey(key);
      window.setTimeout(() => {
        setCopiedRequestKey(current => (current === key ? null : current));
      }, 2_000);
    } catch {
      // Clipboard unavailable (permissions/jsdom) — the mailto link still works.
    }
  };

  const renderOemRequestActions = (
    key: string,
    manufacturer: string,
    model: string,
    trainConfiguration: string,
    requestedItems: readonly string[],
    siteNetMWPerBlock: number | null,
    contactEmail?: string,
  ): React.JSX.Element => {
    const email = buildOemDataRequestEmail({
      manufacturer,
      model,
      trainConfiguration,
      siteName,
      blockCount: currentBlockCount,
      targetNetMW: siteNetMWPerBlock == null ? null : siteNetMWPerBlock * currentBlockCount,
      requestedItems,
      ...(contactEmail ? { contactEmail } : {}),
    });
    return (
      <span className="inline-flex items-center gap-2">
        <a
          aria-label={`Email ${manufacturer} a data and availability request for the ${model}`}
          className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-primary underline-offset-2 hover:underline"
          href={email.mailtoHref}
        >
          <Mail className="h-3 w-3" /> Request OEM data
        </a>
        <button
          type="button"
          aria-label={`Copy the ${model} OEM data request to the clipboard`}
          className="text-[9px] uppercase tracking-wider text-text-muted underline-offset-2 hover:underline"
          onClick={() => { void copyRequest(key, email.to, email.subject, email.body); }}
        >
          {copiedRequestKey === key ? 'Copied' : 'Copy'}
        </button>
      </span>
    );
  };

  // Constraint-fit chips: the row's own solver result projected onto the
  // documented site envelope. `not-applicable` fits stay hidden — no noise.
  const renderConstraintFitChips = (
    cardKey: string,
    result: GenerationImpactResult | null,
  ): React.JSX.Element | null => {
    if (!constraintContext || !result) return null;
    const fits = (cardFits.get(cardKey) ?? [])
      .filter(fit => fit.status !== 'not-applicable' && !hoistedIds.has(fit.id));
    if (fits.length === 0) return null;
    return (
      <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`generation-equipment-constraint-fits-${cardKey}`}>
        {fits.map(fit => fit.status === 'undocumented' && fitAction(fit)
          ? (
            <button
              key={fit.id}
              type="button"
              title={fit.detail}
              data-testid={`generation-equipment-constraint-fit-${cardKey}-${fit.id}`}
              data-fit-status={fit.status}
              data-fit-destination={needsAcousticSources(fit) ? 'acoustic-sources' : 'site-envelope'}
              onClick={fitAction(fit)}
              className="border border-border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-text-muted underline-offset-2 hover:underline rounded-sm"
            >
              {fit.label} undocumented → {fitDestinationLabel(fit)}
            </button>
          )
          : (
            <span
              key={fit.id}
              title={fit.detail}
              data-testid={`generation-equipment-constraint-fit-${cardKey}-${fit.id}`}
              data-fit-status={fit.status}
              className={`border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                fit.status === 'fits'
                  ? 'border-success/40 bg-success/10 text-success'
                  : fit.status === 'exceeds'
                    ? 'border-danger/40 bg-danger/10 text-danger'
                    : 'border-border text-text-muted'
              } rounded-sm`}
            >
              {fit.label} {fit.status === 'fits' ? 'fits' : fit.status === 'exceeds' ? 'exceeded' : 'undocumented'}
            </span>
          ))}
      </div>
    );
  };

  const renderCatalogCard = (row: GenerationEquipmentScreenRow): React.JSX.Element => {
    const active = selectedBasis?.catalogId === row.catalogId;
    const physicalSource = row.publishedPhysicalReference
      ? row.publishedSources.find(
        source => source.title === row.publishedPhysicalReference?.sourceTitle,
      )
      : undefined;
    return (
      <article
        className={`flex min-w-0 flex-col border border-border border-l-4 bg-card p-4 ${rowTone(row)} ${active ? 'ring-1 ring-inset ring-primary' : ''} rounded-lg`}
        data-equipment-id={row.catalogId}
        key={row.catalogId}
      >
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="font-bold text-text-primary">{row.manufacturer} · {row.model}</h3>
            <p className="mt-1 text-[10px] text-text-muted">{row.trainConfiguration}</p>
          </div>
          <span className={`border px-2 py-1 text-[10px] font-bold uppercase tracking-wider ${active ? 'border-success/40 bg-success/10 text-success' : row.confirmationRequired ? 'border-warning/40 bg-warning/10 text-warning' : 'border-border text-text-muted'} rounded-md`}>
            {active ? 'Active basis' : row.confirmationRequired ? 'Confirm OEM' : sourceKind(row.sourceKind)}
          </span>
        </div>
        <dl className="mt-3 grid grid-cols-3 gap-px bg-border text-[9px]">
          <div className="bg-background p-2"><dt className="text-text-muted">Published</dt><dd className="mt-1 font-bold text-text-primary">{number(row.publishedOutputMWPerBlock, 1)} MW</dd><dd className="text-[10px] text-text-muted">{ratingScope(row.publishedRatingScope)}</dd></div>
          <div className="bg-background p-2"><dt className="text-text-muted">Solver ISO</dt><dd className="mt-1 font-bold text-text-primary">{number(row.solverIsoGrossMWPerBlock, 1)} MW</dd><dd className="text-[10px] text-text-muted">per block</dd></div>
          <div className="bg-background p-2"><dt className="text-text-muted">N-1 reserve</dt><dd className={`mt-1 font-bold ${row.result.capacity.nMinusOneReserveVerdict === 'pass' ? 'text-success' : 'text-danger'}`}>{signed(row.result.capacity.nMinusOneReserveMarginMW)} MW</dd><dd className="text-[10px] uppercase text-text-muted">reference screen</dd></div>
        </dl>
        {renderConstraintFitChips(row.catalogId, row.result)}
        <p className="mt-3 text-[9px] leading-relaxed text-text-muted"><strong className="text-text-secondary">Open data:</strong> {unresolvedFields(row)}</p>
        <div className="mt-2 flex flex-wrap items-center gap-3 text-[9px]">
          <a className="inline-flex items-center gap-1 text-primary underline-offset-2 hover:underline" href={row.publishedSources[0]?.url} rel="noreferrer" target="_blank"><BookOpen className="h-3 w-3" /> OEM source</a>
          {row.publishedPhysicalReference && (
            <a aria-label={`${row.publishedPhysicalReference.sourceTitle} physical reference`} className="text-text-muted underline-offset-2 hover:underline" href={physicalSource?.url} rel="noreferrer" target="_blank">Physical reference · {number(row.publishedPhysicalReference.lengthM, 1)} × {number(row.publishedPhysicalReference.widthM, 1)} m</a>
          )}
          {renderOemRequestActions(
            row.catalogId,
            row.manufacturer,
            row.model,
            row.trainConfiguration,
            row.unavailableFields,
            row.siteNetMWPerBlock,
          )}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {!readOnly && (
            <button
              type="button"
              aria-label={`Remove ${row.manufacturer} ${row.model} from the equipment set`}
              disabled={actionsDisabled || active}
              onClick={() => onToggleShortlist(row.catalogId, false)}
              className="inline-flex items-center justify-center gap-1 border border-border px-3 py-2 text-[9px] font-bold uppercase tracking-wider text-text-secondary outline-none hover:bg-background focus-visible:ring-2 focus-visible:ring-primary disabled:text-text-muted rounded-md"
            >
              <X className="h-3 w-3" /> Remove from set
            </button>
          )}
          <button
            type="button"
            aria-label={row.confirmationRequired
              ? `Reconcile the published rating for ${row.manufacturer} ${row.model}`
              : `Apply ${row.manufacturer} ${row.model} equipment envelope`}
            disabled={readOnly || actionsDisabled}
            onClick={() => (row.confirmationRequired
              ? onReconcile?.(row.catalogId)
              : onApply(row.catalogId))}
            className="inline-flex items-center justify-center gap-1 border border-primary px-3 py-2 text-[9px] font-bold uppercase tracking-wider text-primary outline-none hover:bg-primary/5 focus-visible:ring-2 focus-visible:ring-primary disabled:border-border disabled:text-text-muted rounded-md"
          >
            {row.confirmationRequired
              ? <><LockKeyhole className="h-3 w-3" /> Reconcile OEM rating</>
              : active
                ? <><Wrench className="h-3 w-3" /> Re-apply envelope</>
                : <>Use as working basis <ArrowRight className="h-3 w-3" /></>}
          </button>
        </div>
      </article>
    );
  };

  const renderUserCard = (row: GenerationUserEquipmentScreenRow): React.JSX.Element => {
    const { entry } = row;
    const usable = row.validationErrors.length === 0 && row.duty != null && row.result != null;
    return (
      <article
        className="flex min-w-0 flex-col border border-border border-l-4 border-l-info bg-card p-4 rounded-lg"
        data-user-equipment-id={entry.id}
        key={entry.id}
      >
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="font-bold text-text-primary">{entry.manufacturer} · {entry.model}</h3>
            <p className="mt-1 text-[10px] text-text-muted">{entry.trainConfiguration}</p>
          </div>
          <span className="border border-info/40 bg-info/10 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-info rounded-md">
            User-supplied · unverified
          </span>
        </div>
        {usable && row.duty && row.result
          ? (
            <dl className="mt-3 grid grid-cols-3 gap-px bg-border text-[9px]">
              <div className="bg-background p-2"><dt className="text-text-muted">Entered</dt><dd className="mt-1 font-bold text-text-primary">{number(entry.ratedOutputMWPerBlock, 1)} MW</dd><dd className="text-[10px] text-text-muted">{ratingScope(entry.ratingScope)}</dd></div>
              <div className="bg-background p-2"><dt className="text-text-muted">Solver ISO</dt><dd className="mt-1 font-bold text-text-primary">{number(row.duty.solverIsoGrossMWPerBlock, 1)} MW</dd><dd className="text-[10px] text-text-muted">per block</dd></div>
              <div className="bg-background p-2"><dt className="text-text-muted">N-1 reserve</dt><dd className={`mt-1 font-bold ${row.result.capacity.nMinusOneReserveVerdict === 'pass' ? 'text-success' : 'text-danger'}`}>{signed(row.result.capacity.nMinusOneReserveMarginMW)} MW</dd><dd className="text-[10px] uppercase text-text-muted">reference screen</dd></div>
            </dl>
          )
          : (
            <p className="mt-3 border border-danger/50 bg-danger/10 px-2 py-1.5 text-[9px] text-danger rounded-md" role="alert">
              Not screenable: {row.validationErrors.join('; ')}
            </p>
          )}
        {renderConstraintFitChips(entry.id, row.result)}
        <p className="mt-3 text-[9px] leading-relaxed text-text-muted">
          <strong className="text-text-secondary">Open data:</strong> all OEM data outstanding — no published source on record.
          {entry.verificationRequested ? ' Nominated for platform verification.' : ''}
        </p>
        {entry.notes && <p className="mt-1 text-[9px] text-text-muted">Notes: {entry.notes}</p>}
        <div className="mt-2 flex flex-wrap items-center gap-3 text-[9px]">
          {renderOemRequestActions(
            entry.id,
            entry.manufacturer,
            entry.model,
            entry.trainConfiguration,
            USER_EQUIPMENT_STANDARD_DATA_REQUESTS,
            row.siteNetMWPerBlock,
            entry.oemContactEmail,
          )}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {!readOnly && (
            <button
              type="button"
              aria-label={`Remove ${entry.manufacturer} ${entry.model} user equipment`}
              disabled={actionsDisabled}
              onClick={() => onRemoveUserEquipment(entry.id)}
              className="inline-flex items-center justify-center gap-1 border border-border px-3 py-2 text-[9px] font-bold uppercase tracking-wider text-text-secondary outline-none hover:bg-background focus-visible:ring-2 focus-visible:ring-primary disabled:text-text-muted rounded-md"
            >
              <Trash2 className="h-3 w-3" /> Remove
            </button>
          )}
          <button
            type="button"
            aria-label={`Apply ${entry.manufacturer} ${entry.model} user equipment as the working duty`}
            disabled={readOnly || actionsDisabled || !usable}
            onClick={() => onApplyUserEquipment(entry.id)}
            className="inline-flex items-center justify-center gap-1 border border-primary px-3 py-2 text-[9px] font-bold uppercase tracking-wider text-primary outline-none hover:bg-primary/5 focus-visible:ring-2 focus-visible:ring-primary disabled:border-border disabled:text-text-muted rounded-md"
          >
            Use as working basis (unverified) <ArrowRight className="h-3 w-3" />
          </button>
        </div>
      </article>
    );
  };

  return (
    <section
      aria-label="Equipment selection"
      className="min-w-0 max-w-full border-2 border-border bg-card"
      data-testid="generation-equipment-envelope-board"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-4 py-3">
        <div className="min-w-0 flex-1">
          <h2 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider">
            <Factory className="h-4 w-4 text-primary" />
            Equipment selection
          </h2>
          <p className="mt-1 max-w-5xl text-[10px] text-text-muted">
            Your working set — picked from the Equipment Library (your BOM, the OEM database, the company catalog, or manual entry). Published OEM ratings keep their native net, gross, or generator-terminal scope; the solver figures are a disclosed planning normalization, not a vendor guarantee, and missing curves, pressure, acoustics, footprint, cooling, CCS, and forced-outage data stay open until the OEM responds.
          </p>
        </div>
        <div className="border border-border bg-background px-3 py-2 text-right text-[9px] uppercase tracking-wider text-text-muted rounded-md">
          <span className="block font-bold text-text-primary">Library basis</span>
          Official OEM + public method sources · retrieved 18 Jul 2026
        </div>
      </div>

      {appliedStatus && (
        <p
          aria-atomic="true"
          aria-live="polite"
          className="sr-only"
          data-testid="generation-equipment-apply-status"
          role="status"
        >
          {appliedStatus}
        </p>
      )}

      <div className="border-b border-border bg-background p-3" data-testid="generation-equipment-working-set">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-text-secondary" data-testid="generation-equipment-shortlist-heading">
            Shortlist · {workingRows.length + userRows.length} candidate{workingRows.length + userRows.length === 1 ? '' : 's'}
            <span className="ml-2 font-normal normal-case tracking-normal text-text-muted">
              {selectedBasis
                ? <>working basis: <strong className="text-text-primary">{selectedBasis.manufacturer} {selectedBasis.model}</strong></>
                : 'no working basis yet — pick one below'}
            </span>
          </h3>
          {!readOnly && (
            <button
              type="button"
              aria-label="Open the Equipment Library (BOM, OEM database, company catalog, manual entry)"
              disabled={actionsDisabled}
              onClick={onOpenLibrary ?? onAddEquipment}
              className="inline-flex items-center gap-1 border border-primary px-3 py-1.5 text-[9px] font-bold uppercase tracking-wider text-primary outline-none hover:bg-primary/5 focus-visible:ring-2 focus-visible:ring-primary disabled:border-border disabled:text-text-muted rounded-md"
              data-testid="generation-equipment-library-open"
            >
              <PlusCircle className="h-3 w-3" /> Equipment library
            </button>
          )}
        </div>
        {bomBridge && bomBridge.unrepresentedCount > 0 && (
          <div
            className="mb-3 flex flex-wrap items-center justify-between gap-2 border border-warning/50 bg-warning/10 px-3 py-2 rounded-md"
            data-testid="generation-equipment-bom-bridge"
            role="note"
          >
            <p className="text-[10px] text-text-secondary">
              <strong className="text-warning">
                {bomBridge.unrepresentedCount} generation line{bomBridge.unrepresentedCount === 1 ? '' : 's'} in your Equipment BOM {bomBridge.unrepresentedCount === 1 ? "isn't" : "aren't"} in this comparison.
              </strong>
              {' '}Review them so procurement and screening stay aligned.
            </p>
            <span className="flex items-center gap-2">
              <button
                type="button"
                onClick={bomBridge.onReview}
                className="border border-warning px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider text-warning hover:bg-warning/10 rounded-md"
                data-testid="generation-equipment-bom-bridge-review"
              >
                Review BOM equipment
              </button>
              <button
                type="button"
                onClick={bomBridge.onDismiss}
                className="text-[9px] uppercase tracking-wider text-text-muted underline-offset-2 hover:underline"
              >
                Not now
              </button>
            </span>
          </div>
        )}
        {hoistedUndocumented.length > 0 && (
          <div
            className="mb-3 flex flex-wrap items-center gap-1.5 border border-dashed border-border px-3 py-2 text-[10px] text-text-muted rounded-md"
            data-testid="generation-equipment-undocumented-limits"
            role="note"
          >
            <span className="font-bold uppercase tracking-wider">Not screenable for any candidate yet:</span>
            {hoistedUndocumented.map(fit => {
              const qualifier = hoistedQualifier(fit);
              const text = qualifier ? `${fit.label} · ${qualifier}` : `${fit.label} · limit undocumented`;
              const action = fitAction(fit);
              return action
                ? (
                  <button
                    key={fit.id}
                    type="button"
                    title={fit.detail}
                    data-testid={`generation-equipment-undocumented-limit-${fit.id}`}
                    data-qualified={qualifier ? 'true' : 'false'}
                    data-fit-destination={needsAcousticSources(fit) ? 'acoustic-sources' : 'site-envelope'}
                    onClick={action}
                    className="border border-border px-1.5 py-0.5 font-bold uppercase tracking-wider underline-offset-2 hover:underline rounded-sm"
                  >
                    {text} → {fitDestinationLabel(fit)}
                  </button>
                )
                : <span key={fit.id} title={fit.detail} data-testid={`generation-equipment-undocumented-limit-${fit.id}`} data-qualified={qualifier ? 'true' : 'false'} className="border border-border px-1.5 py-0.5 font-bold uppercase tracking-wider rounded-sm">{text}</span>;
            })}
            <span>→ complete each where it lives to turn these into pass / exceed screens on every card.</span>
          </div>
        )}
        {workingRows.length === 0 && userRows.length === 0
          ? (
            <p className="border border-dashed border-border px-4 py-4 text-[10px] text-text-muted rounded-md" data-testid="generation-equipment-empty-set">
              No equipment picked yet. Open the Equipment Library to rank your BOM, the OEM database, and the company catalog against this scenario.
            </p>
          )
          : (
            <div className="grid gap-3 xl:grid-cols-2 2xl:grid-cols-3">
              {workingRows.map(renderCatalogCard)}
              {userRows.map(renderUserCard)}
            </div>
          )}
      </div>

      {selectedBasis && selectedUnit && (
        <div
          className="border-b border-border bg-background px-4 py-3"
          data-testid="generation-equipment-duty-envelope"
        >
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <p className="text-[9px] font-bold uppercase tracking-wider text-text-muted">
              Active duty envelope · {selectedBasis.manufacturer} {selectedBasis.model}
            </p>
            <span className={`border px-2 py-1 text-[9px] font-bold uppercase ${selected.alignmentStatus === 'aligned' ? 'border-success/40 bg-success/10 text-success' : 'border-warning/50 bg-warning/10 text-warning'} rounded-md`}>
              {selected.alignmentStatus === 'aligned' ? 'Catalog aligned' : 'Modified after apply'}
            </span>
          </div>
          <div className="grid gap-px bg-border text-[10px] sm:grid-cols-3 xl:grid-cols-6">
            <div className="bg-card p-2.5"><span className="block text-text-muted">OEM published</span><strong className="mt-1 block text-sm">{number(selectedBasis.publishedRating.outputMWPerBlock, 1)} MW</strong><span className="text-[9px] text-text-muted">{selectedBasis.publishedRating.ratingScope.replaceAll('-', ' ')}</span></div>
            <div className="bg-card p-2.5"><span className="block text-text-muted">Solver ISO gross</span><strong className="mt-1 block text-sm">{number(selectedBasis.screeningNormalization.solverIsoGrossMWPerBlock, 1)} MW</strong><span className="text-[9px] text-text-muted">normalized / block</span></div>
            <div className="bg-card p-2.5"><span className="block text-text-muted">Site gross</span><strong className="mt-1 block text-sm">{number(selectedUnit.siteGrossMWPerUnit, 1)} MW</strong><span className="text-[9px] text-text-muted">ambient × degradation</span></div>
            <div className="bg-card p-2.5"><span className="block text-text-muted">Site net</span><strong className="mt-1 block text-sm">{number(selectedUnit.netMWPerUnit, 1)} MW</strong><span className="text-[9px] text-text-muted">after auxiliary + CCS</span></div>
            <div className="bg-card p-2.5"><span className="block text-text-muted">N-1 fleet net</span><strong className="mt-1 block text-sm">{number(currentResult.capacity.nMinusOneNetMW, 1)} MW</strong><span className="text-[9px] text-text-muted">{Math.max(0, selectedBlockCount - 1)} block(s) online</span></div>
            <div className={currentResult.capacity.nMinusOneReserveVerdict === 'pass' ? 'bg-success/10 p-2.5' : 'bg-warning/10 p-2.5'}><span className="block text-text-muted">N-1 + reserve</span><strong className="mt-1 block text-sm">{signed(currentResult.capacity.nMinusOneReserveMarginMW)} MW</strong><span className="text-[9px] uppercase text-text-muted">{currentResult.capacity.nMinusOneReserveVerdict}</span></div>
          </div>
          <p className="mt-2 text-[9px] text-text-muted" data-testid="generation-equipment-heat-rate-trace">
            Heat-rate trace · published {selectedBasis.publishedRating.heatRateValue == null
              ? 'not available'
              : `${number(selectedBasis.publishedRating.heatRateValue)} ${selectedBasis.publishedRating.heatRateUnit ?? ''} ${selectedBasis.publishedRating.heatingValueBasis ?? ''}`}
            {selectedBasis.publishedRating.efficiencyPct == null
              ? ''
              : ` · ${number(selectedBasis.publishedRating.efficiencyPct, 1)}% efficiency`}
            {' → '}solver {number(selectedBasis.screeningNormalization.solverHeatRateBtuPerKWhHhv)} Btu/kWh HHV on gross output
            {selectedBasis.screeningNormalization.naturalGasLhvToHhvRatio == null
              ? ''
              : ` · LHV/HHV ${selectedBasis.screeningNormalization.naturalGasLhvToHhvRatio.toFixed(2)}`}
          </p>
          {selected.alignmentNotes.length > 0 && (
            <p className="mt-2 text-[10px] text-warning">{selected.alignmentNotes.join(' ')}</p>
          )}
        </div>
      )}

      <p className="border-t border-border px-4 py-2 text-[10px] text-text-secondary">
        <span className="mr-1 font-bold uppercase tracking-wider text-text-muted">Contingency discipline</span>
        Combined-cycle envelopes remove one full 1×1 block. The modular-engine envelope removes one full 24-engine electrical island. Forced-outage probability is intentionally unset until contractual availability, maintenance, and common-mode inputs are supplied.
      </p>
    </section>
  );
}
